import React, {useState} from "react";
import {Box, ButtonBase, Collapse, Drawer, Stack, Typography} from "@mui/material";
import {NavLink, useLocation} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {ArrowDown01Icon, CircleQuestionMarkIcon} from "@hugeicons/core-free-icons";
import {registrySidebarMenuItems} from "../../pages/registry-project/registry-project-routes";

const ACCENT = "#8491F7";

const rowSx = (active, nested) => ({
  width: "100%", justifyContent: "flex-start", gap: 1.5, height: 44, px: 1.5, pl: nested ? 5 : 1.5, borderRadius: "12px",
  fontSize: 14, fontWeight: active ? 700 : 500, textAlign: "left", textDecoration: "none",
  color: active ? "#4B55C9" : "#23264D",
  bgcolor: active ? "rgba(132, 145, 247, 0.14)" : "transparent",
  transition: "background-color .15s ease",
  "&:hover": {bgcolor: active ? "rgba(132, 145, 247, 0.14)" : "#FAFAFD"},
  "&.Mui-disabled": {opacity: 0.45},
});

const isActivePath = (pathname, to) => Boolean(to) && (pathname === to || pathname.startsWith(`${to}/`));

const NavRow = ({item, context, pathname, nested, onNavigate}) => {
  const to = item.to?.(context);
  const disabled = typeof item.disabled === "function" ? item.disabled(context) : false;
  const active = item.path === "" ? pathname === to : isActivePath(pathname, to);
  return (
    <ButtonBase
      component={NavLink}
      end
      to={to}
      disabled={disabled}
      onClick={onNavigate}
      sx={rowSx(active, nested)}
    >
      <Box sx={{display: "flex", flexShrink: 0}}>{item.icon}</Box>
      {item.label}
    </ButtonBase>
  );
};

const SubmenuRow = ({item, context, pathname, role, onNavigate}) => {
  const subItems = item.submenu.filter(sub => sub.roles.includes(role));
  const childActive = subItems.some(sub => isActivePath(pathname, sub.to?.(context)));
  const [open, setOpen] = useState(childActive);
  if (!subItems.length) return null;
  return (
    <>
      <ButtonBase onClick={() => setOpen(current => !current)} sx={rowSx(childActive && !open, false)}>
        <Box sx={{display: "flex", flexShrink: 0}}>{item.icon}</Box>
        <Box component={"span"} sx={{flex: 1}}>{item.label}</Box>
        <Box sx={{
          display: "flex", transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}>
          <HugeiconsIcon icon={ArrowDown01Icon} size={16}/>
        </Box>
      </ButtonBase>
      <Collapse in={open} unmountOnExit>
        <Stack gap={0.5}>
          {subItems.map(sub => (
            <NavRow key={sub.label} item={sub} context={context} pathname={pathname} nested onNavigate={onNavigate}/>
          ))}
        </Stack>
      </Collapse>
    </>
  );
};

export const MobileNavDrawer = ({open, onClose, project, subProject, role, basePath, onStartJourney}) => {
  const {pathname} = useLocation();
  const context = {basePath, project, subProject};
  const items = registrySidebarMenuItems
    .filter(item => item.roles.includes(role))
    .filter(item => !item.hideOnMobile);

  return (
    <Drawer
      anchor={"left"}
      open={open}
      onClose={onClose}
      PaperProps={{sx: {width: 280, maxWidth: "85vw", bgcolor: "#fff", borderRadius: "0 20px 20px 0", boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)"}}}
    >
      <Stack sx={{height: "100%", p: 2}}>
        <Box sx={{px: 1.5, pb: 1.5}}>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: ACCENT, textTransform: "uppercase"}}>
            Menu
          </Typography>
          <Typography noWrap sx={{mt: 0.5, fontSize: 16, fontWeight: 700, color: "#23264D"}}>
            {project?.name || "Project"}
          </Typography>
        </Box>

        <Stack gap={0.5} sx={{flex: 1, minHeight: 0, overflowY: "auto"}}>
          {items.map(item => item.submenu
            ? <SubmenuRow key={item.label} item={item} context={context} pathname={pathname} role={role} onNavigate={onClose}/>
            : <NavRow key={item.label} item={item} context={context} pathname={pathname} onNavigate={onClose}/>)}
        </Stack>

        <ButtonBase
          onClick={() => {
            onClose();
            onStartJourney?.();
          }}
          sx={{...rowSx(false, false), mt: 1}}
        >
          <Box sx={{display: "flex", flexShrink: 0}}><HugeiconsIcon icon={CircleQuestionMarkIcon} size={20} strokeWidth={1.5}/></Box>
          Help &amp; guide
        </ButtonBase>
      </Stack>
    </Drawer>
  );
};
